import React from 'react';
import { MegaMenu } from './MegaMenu';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import { Link } from 'react-router-dom';

const CorporateTravelPage = () => {
  const services = [
    {
      icon: '✈️',
      title: 'International Business Tours',
      description: 'Customized international travel packages for corporate teams, including flights, accommodation, and ground transportation.'
    },
    {
      icon: '🏢',
      title: 'Conference & Event Management',
      description: 'Complete event planning and execution for corporate conferences, seminars, and business meetings worldwide.'
    },
    {
      icon: '🤝',
      title: 'Team Building Retreats',
      description: 'Engaging team-building experiences combined with educational tours to strengthen corporate culture and collaboration.'
    },
    {
      icon: '🎯',
      title: 'Industry-Specific Tours',
      description: 'Specialized tours to global business hubs, factories, and institutions relevant to your industry sector.'
    },
    {
      icon: '📊',
      title: 'Executive Training Programs',
      description: 'Professional development programs with visits to leading global companies and educational institutions.'
    },
    {
      icon: '🌍',
      title: 'Global Business Exposure',
      description: 'Exposure trips to international markets, trade shows, and business ecosystems for strategic insights.'
    }
  ];

  const destinations = [
    {
      name: 'Silicon Valley, USA',
      image: '/images/Silicon.png',
      description: 'Tech innovation and startup ecosystem'
    },
    {
      name: 'Dubai, UAE',
      image: '/images/Dubai.png',
      description: 'Business hub and trade excellence'
    },
    {
      name: 'Singapore',
      image: '/images/Singapore.png',
      description: 'Financial center and smart city solutions'
    },
    {
      name: 'London, UK',
      image: '/images/London.png',
      description: 'Finance, culture, and innovation'
    }
  ];

  const benefits = [
    {
      title: 'Tailored Itineraries',
      description: 'Custom-designed programs aligned with your company\'s objectives and industry focus',
      icon: '📋'
    },
    {
      title: 'Professional Coordination',
      description: 'Dedicated team handling all logistics, from visa assistance to on-ground support',
      icon: '👥'
    },
    {
      title: 'Networking Opportunities',
      description: 'Access to industry leaders, local businesses, and professional networks',
      icon: '🔗'
    },
    {
      title: 'Cost-Effective Solutions',
      description: 'Group rates and strategic partnerships ensuring best value for your investment',
      icon: '💰'
    },
    {
      title: 'Educational Value',
      description: 'Learning experiences that translate to improved business outcomes',
      icon: '📚'
    },
    {
      title: '24/7 Support',
      description: 'Round-the-clock assistance throughout your corporate travel journey',
      icon: '🕐'
    }
  ];

  return (
    <div className="overflow-x-hidden">
      <MegaMenu />

      {/* Hero Section */}
      <div className="relative h-[100vh]">
        {/* Background Image */}
        <div className="absolute inset-0 h-full">
          <img
            src="/dest.jpg"
            alt="Corporate Travel Background"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Overlay Content */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6 md:px-8 lg:px-16 bg-black bg-opacity-60 space-y-4">
          <h1 className="text-white text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-bold leading-tight lg:leading-snug noto">
            Corporate Travel Solutions
          </h1>
          <p className="text-white text-lg md:text-xl max-w-3xl">
            Elevating Business Through Strategic Global Experiences
          </p>
          <p className="text-white hidden sm:block max-w-4xl">
            Transform your corporate vision into reality with our comprehensive travel solutions. <br />
            From business conferences to industry tours, we create experiences that drive growth and innovation.
          </p>
          <Link to="/contact" className="w-full sm:w-auto">
            <button className="bg-blue-400 text-white px-4 mt-5 sm:px-6 py-[10px] rounded-full font-medium shadow-lg hover:bg-blue-500 hover:text-white transition-all sm:w-60 md:w-72 lg:w-[330px] poppins text-xs sm:text-sm md:text-base">
              Get a Custom Quote
            </button>
          </Link>
        </div>
      </div>

      {/* Introduction Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-4xl lg:text-4xl notoh font-bold text-center pb-6 text-gray-800">
          Your Trusted Partner in Corporate Travel
        </h2>
        <p className="text-gray-600 leading-relaxed text-base lg:text-lg text-center px-4 sm:px-8 lg:px-16 xl:px-24 max-w-5xl mx-auto mb-8">
          At ApplicationUni, we understand that corporate travel is more than just transportation and accommodation. 
          It's about creating meaningful business experiences that foster growth, innovation, and collaboration. 
          Our comprehensive corporate travel solutions cater to businesses of all sizes, from startups to multinational corporations.
        </p>
        <p className="text-gray-600 leading-relaxed text-base lg:text-lg text-center px-4 sm:px-8 lg:px-16 xl:px-24 max-w-5xl mx-auto">
          With decades of combined experience in corporate travel management and educational tours, we bring 
          unparalleled expertise in organizing business trips, conferences, incentive travel, and industry-specific 
          educational tours across the globe.
        </p>
      </section>

      {/* Services Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-4xl lg:text-4xl notoh font-bold text-center pb-12 text-gray-800">
            Our Corporate Travel Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md p-8 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-5xl mb-4 text-center">{service.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed text-center">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Destinations Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-4xl lg:text-4xl notoh font-bold text-center pb-12 text-gray-800">
          Popular Corporate Destinations
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {destinations.map((dest, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300"
            >
              <img
                src={dest.image}
                alt={dest.name}
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2">{dest.name}</h3>
                <p className="text-gray-600 text-sm">{dest.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-blue-900 text-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-4xl lg:text-4xl notoh font-bold text-center pb-12">
            Why Choose ApplicationUni for Corporate Travel?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-blue-800 rounded-lg p-6 hover:bg-blue-700 transition-colors duration-300">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-blue-100 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-2xl md:text-4xl lg:text-4xl notoh font-bold text-center pb-12 text-gray-800">
          Industries We Serve
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {[
            'Technology & IT',
            'Finance & Banking',
            'Healthcare',
            'Manufacturing',
            'Education',
            'Hospitality',
            'Retail',
            'Energy & Utilities',
            'Pharmaceuticals',
            'Automotive',
            'Consulting',
            'Real Estate'
          ].map((industry, index) => (
            <div
              key={index}
              className="bg-blue-50 rounded-lg p-4 text-center hover:bg-blue-100 transition-colors duration-300"
            >
              <p className="text-gray-800 font-semibold">{industry}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-4xl lg:text-4xl notoh font-bold text-center pb-12 text-gray-800">
            Our Seamless Process
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {[
                {
                  step: '01',
                  title: 'Consultation',
                  description: 'We discuss your objectives, budget, and preferences to understand your requirements'
                },
                {
                  step: '02',
                  title: 'Proposal',
                  description: 'Our team creates a detailed itinerary and cost breakdown tailored to your needs'
                },
                {
                  step: '03',
                  title: 'Planning',
                  description: 'Upon approval, we handle all bookings, visas, and logistical arrangements'
                },
                {
                  step: '04',
                  title: 'Execution',
                  description: 'Your team travels with our support team managing everything seamlessly'
                },
                {
                  step: '05',
                  title: 'Follow-up',
                  description: 'We gather feedback and ensure complete satisfaction with your experience'
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
                  <div className="bg-blue-500 text-white font-bold text-2xl rounded-full w-16 h-16 flex items-center justify-center flex-shrink-0 mr-6">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 px-3 lg:px-0">
        <div className="max-w-screen-lg mx-auto bg-gradient-to-r from-blue-600 to-blue-800 text-center text-white p-8 sm:p-12 rounded-lg shadow-2xl">
          <h2 className="text-2xl lg:text-4xl font-bold mb-4">
            Ready to Transform Your Corporate Travel?
          </h2>
          <p className="text-base lg:text-xl leading-relaxed max-w-3xl mx-auto mb-8">
            Let us handle the complexities of corporate travel while you focus on what matters most - your business success. 
            Contact us today for a personalized consultation and discover how we can elevate your corporate travel experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold shadow-lg hover:bg-gray-100 transition-all">
                Contact Us
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default CorporateTravelPage;

